import { Route } from '@/app/routes/(app)/recommendation-management/main-page/$store-list';
import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { StoreList } from '@/services/mainPage/storeList/storeList.types';
import * as React from 'react';
import { ManageRecommendationForm } from '../form/ManageRecommendationForm';
import useDeleteMainPageStore from '../hooks/useDeleteMainPageStore';
import { getColumns } from './Column';

export function StoreListTable({
  data,
  pagination,
  loading,
}: {
  data: StoreList[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { t } = useDynamicLocale();
  const params = Route.useParams();
  const groupId = params['store-list'];
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync } = useDeleteMainPageStore();
  const { locationId } = Route.useSearch();
  const columns = React.useMemo(
    () => getColumns(t, groupId, openConfirmation, mutateAsync, locationId),
    [t, groupId, openConfirmation, mutateAsync, locationId],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const addedClubIds = data.map((val) => val.clubId);
  const addedDisplayOrders = data.map((val) => val.displayOrder);
  const canAddStore = useCan({ allOf: [PERMS.recommendation.mainPage.host.create] });

  const onAddNew = () => {
    onOpen({
      title: 'Manage Recommendation',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'common.page.modal.manageRecommendation.title',
      component: (
        <ManageRecommendationForm
          groupId={groupId}
          addedClubIds={addedClubIds}
          locationId={locationId}
          addedDisplayOrders={addedDisplayOrders}
        />
      ),
    });
  };

  const { table } = useDataTable({
    data: data,
    columns,
    pageCount: pageCount,
    meta: {
      title: t('recommendationManagement.storeList.title'),
      totalPage: pagination.totalItems,
      loading: loading,
      actionKey: canAddStore ? 'onAddNew' : '',
      action: onAddNew,
    },
    getRowId: (originalRow) => String(originalRow.recommendationId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
